import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Box, styled,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
  AlertColor,
  Tooltip,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  addGroup, getGroups, getGroupsPerOrganization, getOrganization, getSession, getCompetition, removeGroup,
  startSession, stopSession, scheduleSession, getScheduledSession, updateSessionEndDate,
  resumeSession,
  getAllUsers,
  UpdateGroupOwner,
  getGroup
} from '../../Services/BorgAPIClient';
import moment from 'moment';
import ChangeConfirmationDialog from '../../Components/ChangeConfirmationDialog';
import { LoggedInUserContext } from '../../Components/Admin/AdminPageContainer'; // Import context
import { Roles } from 'src/Constants/BorgEnums';
import CreateGroupModal from 'src/Components/Admin/CreateGroupModal';
import EditSessionDateModal from 'src/Components/Admin/EditSessionDateModal';
import { Group, User } from 'src/Constants/Types';

// The Group type with additional metadata for tracking session data
type GroupComposite = Group & {
  activeSession: {
    id: string,
    groupId: string,
    name: string,
    startDate: Date;
    endDate: Date | null;
    scheduledEndDate?: Date | null;
  };
  lastActiveSession?: {
    id: string;
    name: string;
    endDate: Date;
  };
  scheduledSession?: {
    id: string;
    name: string;
    scheduledStartDate: Date;
    scheduledEndDate?: Date | null;
  };
};

type UserComposite = User & {
  organizationId: string;
}

const CustomTableHead = styled(TableHead)({
  backgroundColor: 'black',
  '& th': {
    color: 'white',
    cursor: 'pointer',
  },
});

type Props = { onNotification: (message: string, notificationType?: AlertColor) => void };

const GroupsAdminPage = (props: Props) => {
  const [currentGroupList, setCurrentGroupList] = useState<GroupComposite[]>([]);
  const [groupDataForGroupList, setGroupDataForGroupList] = useState<GroupComposite[] | undefined>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [openConfirmResumeDialog, setOpenConfirmResumeDialog] = useState(false);
  const [groupToConfirm, setGroupToConfirm] = useState<GroupComposite | null>(null);
  const [sortBy, setSortBy] = useState<keyof GroupComposite>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [disableAllUsers, setDisableAllUsers] = useState(false);
  const [openEndTermDialog, setOpenEndTermDialog] = useState(false);
  const [openEditStartDateDialog, setOpenEditStartDateDialog] = useState(false);
  const [groupToEditStartDate, setGroupToEditStartDate] = useState<GroupComposite | null>(null);
  const [openEditEndDateDialog, setOpenEditEndDateDialog] = useState(false);
  const [groupToEditEndDate, setGroupToEditEndDate] = useState<GroupComposite | null>(null);
  const [instructorList, setInstructorList] = useState<UserComposite[]>([]);

  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;
  const updateAdminGroupList = useContext(LoggedInUserContext)?.updateAdminGroupList;

  const fetchGroups = useCallback(async () => {
    if (loggedInUser)  //if logged in user has been obtained
    {
      let result;
      try {
        if (loggedInUser.roles[0] === Roles.SystemAdmin) //If logged in user is system admin then return all groups
          result = await getGroups();
        else if (loggedInUser.roles[0] === Roles.OrganizationAdmin) //If organization admin return all groups in the admins organization
        {
          //Get organization id
          var loggedInUserOrganizationData = await getOrganization(loggedInUser.organizationId);
          result = await getGroupsPerOrganization(loggedInUserOrganizationData.data.id);
        }
        else if (loggedInUser.roles[0] === Roles.Instructor) //If instructor return all groups owned by instructor
        {
          //Get group per owner id
          result = await getGroups(loggedInUser.id);

        }

        setGroupDataForGroupList(result.data);  //Store the group data portion of the group list
      } catch (error : any) {
        const errorMsg = `Failed to get groups: ${error.title}`;
        console.error(errorMsg);
        props.onNotification(errorMsg, "error");
      }
    }
  }, [loggedInUser]);

  const fetchSession = async () => {
    if (groupDataForGroupList && groupDataForGroupList.length > 0) {

      const newGroupList: GroupComposite[] = [];
      for (let i = 0; i < groupDataForGroupList.length; i++) {

        const group = groupDataForGroupList[i];
        newGroupList[i] = group;

        // try to get the active (current) session
        let activeSession;
        try {
          activeSession = await getSession(group.id);
        } catch (error) {
          console.log(`Failed to fetch the active session for group ${group.id}`);
        }

        if (activeSession?.data) {
          // if that was successful, attach the active session to the group it belongs with
          newGroupList[i] = { ...group, activeSession: activeSession.data };

        } else if (group.lastActiveSessionId) {

          // if no active session, but there is a lastActiveSessionId, then try to get the last active (ended) session
          let lastActiveSession;
          try {
            lastActiveSession = await getCompetition(group.lastActiveSessionId);
          } catch (error) {
            console.log(`Failed to fetch the last active session for group ${group.id}`);
          }

          if (lastActiveSession?.data) {
            // if that was successful, attach the last active session to the group it belongs with
            newGroupList[i] = { ...group, lastActiveSession: lastActiveSession.data };
          }
        }

        // Fetch the scheduled session
        if (group.scheduledSessionId) {
          let scheduledSession;
          try {
            scheduledSession = await getScheduledSession(group.id);
          } catch (error) {
            console.log(`Failed to fetch the scheduled session for group ${group.id}`);
          }

          if (scheduledSession?.data) {
            // if that was successful, attach the scheduled session to the group it belongs with
            newGroupList[i] = { ...group, scheduledSession: scheduledSession.data };
          }
        }
      }

      // Check if newGroupList is different from currentGroupList
      const groupDataForGroupListJson = JSON.stringify(groupDataForGroupList);
      const newGroupListJson = JSON.stringify(newGroupList);

      if (groupDataForGroupListJson !== newGroupListJson || currentGroupList.length === 0) {//Only execute setCurrentGroupList if newGroup has changed or currentGroupList hasn't been set (initial load)
        setCurrentGroupList(newGroupList);
      }
    } 
    
    if (groupDataForGroupList) {
      setIsLoaded(true);
    }
  };

  const fetchInstructorList = async () => {
    //Fetch instructor list so the instructor can be set only if user has admin rights
    if (loggedInUser?.roles[0] === Roles.SystemAdmin || loggedInUser?.roles[0] === Roles.OrganizationAdmin) {
      await fetchUsersAndFilterInstructors();
    }
  };

  const fetchUsersAndFilterInstructors = async () => {
    if (loggedInUser)  //if logged in obtain users
    {
      let instructors;
      try {
        let result: { data: UserComposite[]; }; //initialize with blank list of users
        result = await getAllUsers(loggedInUser.organizationId);
        // Filter out users based on roles
        instructors = result.data.filter(user =>
          user.roles.includes(Roles.Instructor) ||
          user.roles.includes(Roles.SystemAdmin) ||
          user.roles.includes(Roles.OrganizationAdmin)
        );

        if (instructors) {
          setInstructorList(instructors);
        }
      } catch (error) {
        console.error(`Error fetching users:`, error);
      }
    }
  }

  useEffect(() => {
    fetchGroups();  //Obtain initial info for table
  }, [fetchGroups]);

  useEffect(() => {
    fetchSession();
    fetchInstructorList();
  }, [groupDataForGroupList]); //After updated group list is obtained execute fetchSession and fetchInstructorList

  const closeConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog(false);
  };

  const closeConfirmResumeDialog = () => {
    setOpenConfirmResumeDialog(false);
  };

  const toggleCreateGroupModal = () => {
    setIsCreateGroupOpen(!isCreateGroupOpen);
  }

  //Sorting functions
  const handleSort = (column: keyof Group) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    setSortBy(column);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  const sortedGroups = [...currentGroupList].sort((a, b) => {
    const compareResult = (aVal: any, bVal: any) => {
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    };

    const result = compareResult(a[sortBy], b[sortBy]);
    return sortDirection === 'asc' ? result : -result;
  });

  //Event Functions
  const handleCreateGroupConfirm = async (groupName: string) => {
    const fullGroupData = {
      name: groupName,
      ownerId: loggedInUser?.id ?? '',
      caption: "",
      memberIds: [],
    };

    let newGroupId;
    //API Call to POST new Group
    try {
      newGroupId = await addGroup(fullGroupData);

    } catch (err: any) {
      const errorMsg = `Failed to create group: ${err.title}`;
      console.error(errorMsg);
      props.onNotification(errorMsg, "error");
    }

    //If the group was created successfully, fetch the new group data
    if (newGroupId) {
      //API Call to get and set new group data
      try {
        //Obtain new group and add to list
        const newGroup = (await getGroup(newGroupId)).data
  
        // Update the group list in state directly
        const updatedGroupList = [...currentGroupList, newGroup];
        setCurrentGroupList(updatedGroupList);
      } catch (err: any) {
        const errorMsg = `Failed to update group list: ${err.title}`;
        console.error(errorMsg);
        props.onNotification(errorMsg, "error");
      } 
    }
    else {
      console.error('Failed to create group: No new group ID returned.');
    }

    toggleCreateGroupModal(); //Close modal
  }

  const handleGradebookClick = (group: GroupComposite) => {
    let url;
    if (group.activeSession) {
      url = `/gradebook/${group.activeSession.id}`;
    } else if (group.lastActiveSession) {
      url = `/gradebook/${group.lastActiveSession.id}`;
    }
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleStartSession = async (groupId: string) => {
    try {
      await startSession(groupId);
      fetchGroups();  //Update table

      // Update the group list in context - only when starting now (not a scheduled start)
      if (updateAdminGroupList) {
        updateAdminGroupList(groupDataForGroupList as Group[]);
      }
    } catch (err: any) {
      const errorMsg = `Failed to start session: ${err.title}`;
      console.error(errorMsg);
      props.onNotification(errorMsg, "error");
    }
  }

  const handleScheduleSession = async (groupId: string, newDate: Date) => {
    try {
      const newDateStr = newDate.toISOString().split('.')[0]; // Remove milliseconds
      await scheduleSession(groupId, newDateStr);
      fetchGroups();  //Update table
    } catch (err: any) {
      const errorMsg = `Failed to schedule session: ${err.title}`;
      console.error(errorMsg);
      props.onNotification(errorMsg, "error");
    }
  }

  const handleEndTerm = async () => {
    if (groupToEditEndDate) {
      try {
        await stopSession(groupToEditEndDate.id, disableAllUsers);
        fetchGroups();  //Update table
      } catch (err: any) {
        const errorMsg = `Failed to stop session: ${err.title}`;
        console.error(errorMsg);
        props.onNotification(errorMsg, "error");
      }
    }
    setDisableAllUsers(false);
    setOpenEndTermDialog(false);
    setOpenEditEndDateDialog(false);
    setGroupToEditEndDate(null);
  }

  const handleCancelEndTerm = async () => {
    setDisableAllUsers(false);
    setOpenEndTermDialog(false);
  }

  const handleDeleteClick = (group: GroupComposite) => {
    setGroupToConfirm(group);
    setOpenConfirmDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (groupToConfirm) {
      try {
        await removeGroup(groupToConfirm.id);
        // Update the state directly to remove the deleted group
        setCurrentGroupList(currentGroupList.filter((group) => group.id !== groupToConfirm.id));
      } catch (err: any) {
        console.log(err);
        const errorMsg = `Failed to delete course: ${err.title}`;
        console.error(errorMsg);
        props.onNotification(errorMsg, "error");
      }
    }
    setOpenConfirmDeleteDialog(false);
    setGroupToConfirm(null);

    // Update the group list in context
    if (updateAdminGroupList) {
      updateAdminGroupList(groupDataForGroupList as Group[]);
    }
  };

  const handleEditStartDateClick = (group: GroupComposite) => {
    setGroupToEditStartDate(group);
    setOpenEditStartDateDialog(true);
  };

  const handleResumeClick = (group: GroupComposite) => {
    setGroupToConfirm(group);
    setOpenConfirmResumeDialog(true);
  }

  const handleConfirmResume = async () => {
    if (groupToConfirm) {
      try {
        await resumeSession(groupToConfirm.id, true);
        await fetchGroups();
      } catch (err: any) {
        console.log(err);
        const errorMsg = `Error while resuming class: ${err.title}`;
        console.error(errorMsg);
        props.onNotification(errorMsg, "error");
      }
    }
    setOpenConfirmResumeDialog(false);
    setGroupToConfirm(null);
  };

  const handleEditEndDateClick = (group: GroupComposite) => {
    const { startDate } = group.activeSession ?? {};
    const { scheduledStartDate } = group.scheduledSession ?? {};

    // Only allow "End Now" if session has started
    if (startDate || (scheduledStartDate && isFutureDate(scheduledStartDate))) {
      setGroupToEditEndDate(group);
      setOpenEditEndDateDialog(true);
    } else {
      console.warn('Cannot end session that has not started.');
    }
  };

  const handleEditStartDateConfirm = async (startNow: boolean, scheduledDate: Date | null) => {
    if (groupToEditStartDate) {
      if (startNow) {
        handleStartSession(groupToEditStartDate.id);
      } else if (scheduledDate) {
        handleScheduleSession(groupToEditStartDate.id, scheduledDate);
      }
    }
    setOpenEditStartDateDialog(false);
  };

  const handleEditEndDateConfirm = async (endNow: boolean, scheduledDate: Date | null) => {
    if (!groupToEditEndDate) {
      console.warn('No group selected for editing end date.');
      return;
    }

    if (endNow) {
      setGroupToEditEndDate(groupToEditEndDate);
      setOpenEndTermDialog(true);
    } else {
      try {
        const newDate = scheduledDate
          ? scheduledDate.toISOString().split(".")[0]
          : new Date().toISOString().split(".")[0];

        await updateSessionEndDate(groupToEditEndDate.id, newDate, endNow);
        fetchGroups();
      } catch (error) {
        console.error('Failed to update end date:', error);
      } finally {
        setOpenEditEndDateDialog(false); // Close the edit end date modal dialog
      }
    }
  };

  //Misc Functions
  const calculateTimeAgo = (date: Date) => {
    if (!date) return '';

    return moment(date).fromNow();
  };

  function formatDateForDisplay(date: Date) {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY @ HH:mm')
  }

  function isFutureDate(date: Date) {
    if (!date) return '';
    return moment(date).isAfter(moment())
  }

  const determineStatus = (group: GroupComposite) => {
    const now = new Date();

    if (group.scheduledSession?.scheduledStartDate && new Date(group.scheduledSession.scheduledStartDate) > now) {
      return 'Scheduled';
    } else if (group.activeSession) {
      return 'Active';
    } else if (group.lastActiveSession) {
      return 'Ended';
    } else {
      return 'Inactive'; // Default to 'Inactive' if no other statuses apply
    }
  };

  const getScheduledEndDate = (group: GroupComposite | null) => {

    // return scheduled end date if there is an active session
    if (group?.activeSession?.scheduledEndDate)
      return new Date(group.activeSession.scheduledEndDate);

    // return scheduled end date if there is a scheduled session
    if (group?.scheduledSession?.scheduledEndDate)
      return new Date(group.scheduledSession.scheduledEndDate);

    // otherwise, return null
    return null;
  };

  const getScheduledStartDate = (group: GroupComposite | null) => {

    // return scheduled start date if there is a scheduled session
    if (group?.scheduledSession?.scheduledStartDate)
      return new Date(group.scheduledSession.scheduledStartDate);

    // otherwise, return null
    return null;
  };

  //Render Functions
  const renderStartColumn = (group: GroupComposite) => {
    const { startDate } = group.activeSession ?? {};
    const { scheduledStartDate } = group.scheduledSession ?? {};
    if (scheduledStartDate && isFutureDate(scheduledStartDate)) {
      // If a scheduled session exists and is in the future
      const formattedScheduledDate = formatDateForDisplay(scheduledStartDate);
      const scheduledTimeAgo = calculateTimeAgo(scheduledStartDate);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => handleEditStartDateClick(group)}>
            <EditIcon />
          </IconButton>
          <Box>
            <Box>{`Scheduled for ${formattedScheduledDate}`}</Box>
            <Box>{scheduledTimeAgo}</Box>
          </Box>
        </Box>
      );
    }

    if (!startDate) {
      // If no active session or scheduled session
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => handleEditStartDateClick(group)}>
            <EditIcon />
          </IconButton>
          Schedule/Start New Class
        </Box>
      );
    }

    const isFuture = isFutureDate(startDate);

    if (isFuture) {
      // make editable
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => handleEditStartDateClick(group)}>
            <EditIcon />
          </IconButton>
          <Box>
            <Box>{`Scheduled for ${formatDateForDisplay(startDate)}`}</Box>
            <Box>{calculateTimeAgo(startDate)}</Box>
          </Box>
        </Box>
      );
    } else {
      // non-editable since the class has already started
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box style={{ marginLeft: 40 }}>
            <Box>{`Started on ${formatDateForDisplay(startDate)}`}</Box>
            <Box>{calculateTimeAgo(startDate)}</Box>
          </Box>
        </Box>
      );
    }
  };

  const renderEndColumn = (group: GroupComposite) => {
    const scheduledEndDate = group.scheduledSession?.scheduledEndDate;
    const endDate = group.lastActiveSession?.endDate;
    const activeScheduledEndDate = group.activeSession?.scheduledEndDate;
    const startDate = group.activeSession?.startDate;

    if (endDate) {
      // Session has ended
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="If you didn't mean to end the last class, click this button to resume it. All students will be re-enabled and re-added.">
            <IconButton onClick={() => handleResumeClick(group)}>
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Box>{`Ended on ${formatDateForDisplay(endDate)}`}</Box>
            <Box>Need to resume? Click button.</Box>
          </Box>
        </Box>
      );
    }

    if (activeScheduledEndDate) {
      // Active session with a scheduled end date
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => handleEditEndDateClick(group)}>
            <EditIcon />
          </IconButton>
          <Box>
            <Box>{`Scheduled to end ${formatDateForDisplay(activeScheduledEndDate)}`}</Box>
            <Box>{calculateTimeAgo(activeScheduledEndDate)}</Box>
          </Box>
        </Box>
      );
    }

    if (startDate) {
      // Active session; allow scheduling or ending now
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => handleEditEndDateClick(group)}>
            <EditIcon />
          </IconButton>
          Schedule/End Class
        </Box>
      );
    }

    if (scheduledEndDate) {
      // Scheduled session with a scheduled end date
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => handleEditEndDateClick(group)}>
            <EditIcon />
          </IconButton>
          <Box>
            <Box>{`Scheduled to end ${formatDateForDisplay(scheduledEndDate)}`}</Box>
            <Box>{calculateTimeAgo(scheduledEndDate)}</Box>
          </Box>
        </Box>
      );
    }

    if (group.scheduledSession) {
      // Scheduled session without a scheduled end date
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => handleEditEndDateClick(group)}>
            <EditIcon />
          </IconButton>
          Schedule/End Class
        </Box>
      );
    }

    // No active session and no scheduled end date
    return <Box />;
  };

  const renderOwnerDropDown = (group: Group) => {
    if (instructorList.length > 0 ) {
      return (<OwnerDropdown group={group} /> );
    } else {
      return ( <div>None Available</div> );
    }
  }

  //Custom components:
  const createGroupComponent = (
    <div>
      <Box sx={{
        marginY: 2,
        borderRadius: 1,
      }}
      >
        <Button variant="contained" color="primary"
          onClick={() => toggleCreateGroupModal()}
          startIcon={<AddIcon />} >
          Create Course
        </Button>
      </Box>
      <CreateGroupModal
        isOpen={isCreateGroupOpen}
        onClose={toggleCreateGroupModal}
        onConfirm={handleCreateGroupConfirm}
      />
    </div>
  )

  //Components for Organization Drop down to function.
  interface OwnerDropdownProps {
    group?: Group | null;
  }

  const OwnerDropdown: React.FC<OwnerDropdownProps> = ({ group }) => {
    return (
      <FormControl sx={{ display: 'flex' }} size="small">
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          value={instructorList.find((instructor) => instructor.id === group?.ownerId)?.id || ''} //Filter out instructor name using ownerId
          onChange={(event) => handleOwnerChange(group?.id, event.target.value)}
          variant='standard'
        >
          {instructorList
            .filter(instructor => instructor.organizationId === group?.ownerOrganizationId) //Only show instructors in the same organization as the group
            .map(instructor => (
              <MenuItem key={instructor.id} value={instructor.id}>
                {instructor.username}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    )
  }

  async function handleOwnerChange(groupId?: string | null, ownerId?: string | null): Promise<void> {
    //Update owner id for group
    if (groupId && ownerId) {
      try {
        UpdateGroupOwner(groupId, ownerId);
        //Update table with selection made on the individual cell
        const updatedGroupList = currentGroupList.map(group => {
          if (group.id === groupId) {
            return { ...group, ownerId };
          }
          return group;
        });

        setCurrentGroupList(updatedGroupList);
      } catch (error) {
        console.error(`Error updating group:`, error);
      }
    }
  }

  const endTermDialog = (
    <div>
      <Dialog open={openEndTermDialog} onClose={() => setOpenEndTermDialog(false)}>
        <DialogTitle>End Term</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={<Checkbox checked={disableAllUsers} onChange={(e) => setDisableAllUsers(e.target.checked)} />}
            label="Disable all users in course?" />
          <DialogContentText>Disabled users will not be deleted nor will they be removed from the gradebook for this term.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEndTerm}>Cancel</Button>
          <Button onClick={handleEndTerm} autoFocus> Confirm</Button>
        </DialogActions>
      </Dialog>
    </div>
  )

  const renderGroupNameCell = (group : GroupComposite) => {

    if (group.activeSession || group.lastActiveSession) {
      // an active or previously active session exists, so provide a link to the gradebook
      return (
        <Link
          href="#"
          color="inherit"
          underline="hover"
          onClick={() => handleGradebookClick(group)}
          sx={{ display: 'flex', alignItems: "center", gap: 1 }}
        >
          {group.name} <OpenInNewIcon sx={{ fontSize: '1.25em' }} />
        </Link>
      );

    } else {
      // no active session, so just show the name of the group
      return group.name;
    }
  }

  if (isLoaded && currentGroupList.length > 0) {
    return (
      <div>
        {(loggedInUser?.roles[0] !== Roles.Instructor) && createGroupComponent}
        <Box sx={{
          overflow: 'auto',
          borderRadius: 1,
          marginY: 2,
          bgcolor: 'primary.dark'
        }}>
          <TableContainer component={Paper}>
            <Table>
              <CustomTableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: '200px', align: 'left' }} onClick={() => handleSort('name')}>
                    Course Name {sortBy === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell sx={{ minWidth: '100px', align: 'left' }}>
                    Access Code
                  </TableCell>
                  {(loggedInUser?.roles[0] === Roles.SystemAdmin || loggedInUser?.roles[0] === Roles.OrganizationAdmin) && (
                    <TableCell sx={{ minWidth: '200px', align: 'left' }}>
                      Instructor
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: '60px', align: 'left' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ minWidth: '300px', width: '300px', align: 'right' }}>
                    Start
                  </TableCell>
                  <TableCell sx={{ minWidth: '340px', width: '340px', align: 'right' }}>
                    End
                  </TableCell>
                  {(loggedInUser?.roles[0] === Roles.SystemAdmin || loggedInUser?.roles[0] === Roles.OrganizationAdmin) &&
                    <TableCell style={{ textAlign: 'center' }}>Actions</TableCell>
                  }
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {sortedGroups.map((group, index) => (
                  <TableRow
                    key={index}
                  >
                    <TableCell>
                      <Box>{renderGroupNameCell(group)}</Box>
                    </TableCell>
                    <TableCell>{group.password ?? 'NO CODE'}</TableCell>
                    {(loggedInUser?.roles[0] === Roles.SystemAdmin || loggedInUser?.roles[0] === Roles.OrganizationAdmin) &&
                      <TableCell>
                        {renderOwnerDropDown(group)}
                      </TableCell>
                    }
                    <TableCell>{determineStatus(group)}</TableCell>
                    <TableCell>{renderStartColumn(group)}</TableCell>
                    <TableCell>{renderEndColumn(group)}</TableCell>
                    {(loggedInUser?.roles[0] === Roles.SystemAdmin || loggedInUser?.roles[0] === Roles.OrganizationAdmin) &&
                      <TableCell>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                          <Button startIcon={<DeleteIcon />} onClick={() => handleDeleteClick(group)}>
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    }
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <EditSessionDateModal
          isOpen={openEditStartDateDialog}
          onClose={() => setOpenEditStartDateDialog(false)}
          onConfirm={handleEditStartDateConfirm}
          scheduledDate={getScheduledStartDate(groupToEditStartDate)}
          isEditingEndDate={false}
        />
        <EditSessionDateModal
          isOpen={openEditEndDateDialog}
          onClose={() => setOpenEditEndDateDialog(false)}
          onConfirm={handleEditEndDateConfirm}
          scheduledDate={getScheduledEndDate(groupToEditEndDate)}
          activeSessionStartDate={
            groupToEditEndDate?.activeSession?.startDate
              ? new Date(groupToEditEndDate.activeSession.startDate)
              : null
          }
          scheduledStartDate={
            groupToEditEndDate?.scheduledSession?.scheduledStartDate
              ? new Date(groupToEditEndDate.scheduledSession.scheduledStartDate)
              : null
          }
          isEditingEndDate={true}
        />
        <ChangeConfirmationDialog
          title="Confirm Delete"
          message={`This action cannot be undone. Are you sure you want to delete "${groupToConfirm?.name}"?`}
          open={openConfirmDeleteDialog}
          onClose={closeConfirmDeleteDialog}
          onCancel={closeConfirmDeleteDialog}
          onConfirm={handleConfirmDelete}
        />
        <ChangeConfirmationDialog
          title="Confirm Resume"
          message={"Would you like to resume this class? All students who were last in the class will be re-added and re-enabled. The class will continue as if it had not been ended."}
          open={openConfirmResumeDialog}
          onClose={closeConfirmResumeDialog}
          onCancel={closeConfirmResumeDialog}
          onConfirm={handleConfirmResume}
        />

        {endTermDialog}

      </div >
    );
  } else if (isLoaded) {
    return (
      <div>
        {(loggedInUser?.roles[0] !== Roles.Instructor) && createGroupComponent}
        <Typography align='center' variant='h2' sx={{ margin: 10 }}>No Courses Found</Typography>
      </div>
    );
  } else {
    return <div>Loading...</div>;
  }
};

export default GroupsAdminPage;